import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CompareArrows,
  Inventory2,
  LocalDining,
  Science,
} from '@mui/icons-material';

// const contentsRows = [
//   {
//     name: 'Protein source',
//     reference: 'Dairy protein',
//     alternative: 'Protein isolate',
//     difference: 'Different source',
//   },
//   {
//     name: 'Physical state',
//     reference: 'Solid',
//     alternative: 'Solid',
//     difference: 'Same',
//   },
//   {
//     name: 'Declared allergen',
//     reference: 'Milk',
//     alternative: 'Review',
//     difference: 'Requires review',
//     warning: true,
//   },
// ];

const nutritionRows = [
  {
    name: 'Energy',
    reference: '380 kcal',
    alternative: '390 kcal',
    difference: '+10 kcal',
  },
  {
    name: 'Protein',
    reference: '80 g',
    alternative: '84 g',
    difference: '+4 g',
  },
  {
    name: 'Fat, total',
    reference: '5.5 g',
    alternative: '2.1 g',
    difference: '-3.4 g',
  },
  {
    name: 'Carbohydrate',
    reference: '8.0 g',
    alternative: '6.5 g',
    difference: '-1.5 g',
  },
];

const technicalRows = [
  {
    name: 'Solubility',
    reference: 'High',
    alternative: 'Medium',
    difference: 'Review',
    warning: true,
  },
  {
    name: 'Texture effect',
    reference: 'Creaminess',
    alternative: 'Body support',
    difference: 'Different effect',
  },
];

// function StatusPill({ children, tone = 'default' }) {
//   const isWarning = tone === 'warning';
//
//   return (
//     <Box
//       sx={{
//         px: 0.65,
//         py: 0.28,
//         borderRadius: 999,
//         fontSize: 9.2,
//         fontWeight: 900,
//         lineHeight: 1,
//         whiteSpace: 'nowrap',
//         color: isWarning ? 'warning.dark' : 'primary.main',
//         bgcolor: (theme) =>
//           isWarning
//             ? alpha(theme.palette.warning.main, 0.14)
//             : alpha(theme.palette.primary.main, 0.08),
//         border: '1px solid',
//         borderColor: (theme) =>
//           isWarning
//             ? alpha(theme.palette.warning.main, 0.3)
//             : alpha(theme.palette.primary.main, 0.18),
//       }}
//     >
//       {children}
//     </Box>
//   );
// }

function CompareSection({ icon, title, rows, compact = false }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={0.65}
        alignItems="center"
        sx={{
          px: 1.05,
          py: 0.72,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.035),
        }}
      >
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 900,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.35,
          }}
        >
          {title}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 0.85fr) minmax(0, 0.85fr) minmax(0, 0.9fr)',
          columnGap: 0.75,
          px: 1.05,
          py: 0.62,
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {['Name', 'Reference', 'Target', 'Difference'].map((head) => (
          <Typography
            key={head}
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 800, fontSize: 9.5 }}
          >
            {head}
          </Typography>
        ))}
      </Box>

      {rows.map((row, index) => (
        <Box
          key={row.name}
          sx={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(0, 1.05fr) minmax(0, 0.85fr) minmax(0, 0.85fr) minmax(0, 0.9fr)',
            columnGap: 0.75,
            alignItems: 'center',
            px: 1.05,
            py: compact ? 0.55 : 0.67,
            borderBottom: index !== rows.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            bgcolor: row.warning
              ? (theme) => alpha(theme.palette.warning.main, 0.045)
              : 'background.paper',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 900,
              fontSize: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 800,
              fontSize: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.reference}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 800,
              fontSize: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.alternative}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              fontWeight: 900,
              fontSize: 10,
              color: row.warning ? 'warning.dark' : 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.difference}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

export default function IngredientAlternativesComparisonVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 310,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.15,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CompareArrows sx={{ fontSize: 16 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Ingredient comparison
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Compare ingredient records side by side using available data
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Compare"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Stack>

        {/* Compared ingredients */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.014),
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              gap: 0.9,
              alignItems: 'center',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Inventory2 sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 900,
                      fontSize: 10.8,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Whey protein concentrate
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 9.6,
                      mt: 0.15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    reference ingredient
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <CompareArrows sx={{ fontSize: 18, color: 'primary.main' }} />

            <Paper
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Inventory2 sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 900,
                      fontSize: 10.8,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Pea protein isolate
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 9.6,
                      mt: 0.15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Alternative ingredient
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            p: 1.5,
            display: 'grid',
            gridTemplateRows: 'auto auto',
            gap: 1,
            minHeight: 205,
            overflow: 'hidden',
          }}
        >
          {/*<CompareSection*/}
          {/*  icon={<TableRows sx={{ fontSize: 15 }} />}*/}
          {/*  title="Contents"*/}
          {/*  rows={contentsRows}*/}
          {/*  compact*/}
          {/*/>*/}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1.35fr 0.95fr' },
              gap: 1,
              minWidth: 0,
            }}
          >
            <CompareSection
              icon={<LocalDining sx={{ fontSize: 15 }} />}
              title="Nutrition"
              rows={nutritionRows}
              compact
            />

            <CompareSection
              icon={<Science sx={{ fontSize: 15 }} />}
              title="Technical"
              rows={technicalRows}
              compact
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}